/**
 * Recent Transactions Component
 *
 * Displays a list of recent transactions with icons and status indicators
 */

import React from "react";
import { type Transaction } from "@/adapters/database";

interface RecentTransactionsProps {
  transactions: Transaction[];
  className?: string;
}

export const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  transactions,
  className = "",
}) => {
  const formatTransactionType = (type: Transaction["type"]): string => {
    switch (type) {
      case "buy":
        return "Buy";
      case "sell":
        return "Sell";
      case "transfer":
        return "Transfer";
      case "deposit":
        return "Deposit";
      case "withdrawal":
        return "Withdrawal";
      default:
        return "Transaction";
    }
  };

  const getStatusColor = (status: Transaction["status"]): string => {
    switch (status) {
      case "completed":
        return "text-sa-success";
      case "pending":
        return "text-sa-warning";
      case "failed":
        return "text-sa-danger";
      case "cancelled":
        return "text-sa-text-muted";
      default:
        return "text-sa-text-dim";
    }
  };

  const getStatusBadge = (status: Transaction["status"]): string => {
    switch (status) {
      case "completed":
        return "bg-sa-success/10 text-sa-success";
      case "pending":
        return "bg-sa-warning/10 text-sa-warning";
      case "failed":
        return "bg-sa-danger/10 text-sa-danger";
      case "cancelled":
        return "bg-sa-text-dim/10 text-sa-text-muted";
      default:
        return "bg-sa-text-dim/10 text-sa-text-dim";
    }
  };

  const getTransactionIcon = (type: Transaction["type"]): JSX.Element => {
    switch (type) {
      case "buy":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case "sell":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        );
      case "transfer":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
            />
          </svg>
        );
      case "deposit":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16V4m0 0L3 8m4-4l4 4"
            />
          </svg>
        );
      case "withdrawal":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        );
    }
  };

  const formatTimestamp = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return timestamp.toLocaleDateString();
  };

  if (transactions.length === 0) {
    return (
      <div className={`card p-5 ${className}`}>
        <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
        <div className="text-center py-8 text-sa-text-dim">
          <svg
            className="w-12 h-12 mx-auto mb-4 opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <p>No transactions yet</p>
          <p className="text-sm mt-1">Your transaction history will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`card p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Recent Transactions</h3>
        <button className="text-primary text-sm hover:text-primary-hover transition-colors">
          View all
        </button>
      </div>

      <div className="space-y-3">
        {transactions.map((transaction) => (
          <div
            key={transaction.id}
            className="flex items-center justify-between p-3 rounded-lg hover:bg-sa-surface-hover transition-colors cursor-pointer"
          >
            <div className="flex items-center">
              {/* Transaction type icon */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${getStatusColor(transaction.status)}`}
              >
                {getTransactionIcon(transaction.type)}
              </div>

              {/* Transaction details */}
              <div>
                <div className="flex items-center">
                  <p className="font-medium text-sm">
                    {formatTransactionType(transaction.type)} {transaction.currency}
                  </p>
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${getStatusBadge(transaction.status)}`}
                  >
                    {transaction.status}
                  </span>
                </div>
                <p className="text-xs text-sa-text-dim">
                  {formatTimestamp(transaction.timestamp)}
                  {transaction.txHash && (
                    <span className="ml-1">
                      • {transaction.txHash.slice(0, 6)}...{transaction.txHash.slice(-4)}
                    </span>
                  )}
                </p>
                {transaction.description && (
                  <p className="text-xs text-sa-text-muted mt-0.5">{transaction.description}</p>
                )}
              </div>
            </div>

            {/* Amount and value */}
            <div className="text-right">
              <p
                className={`font-medium text-sm ${
                  transaction.type === "buy" || transaction.type === "deposit"
                    ? "text-sa-success"
                    : transaction.type === "sell" || transaction.type === "withdrawal"
                      ? "text-sa-danger"
                      : "text-sa-text"
                }`}
              >
                {transaction.type === "buy" || transaction.type === "deposit"
                  ? "+"
                  : transaction.type === "sell" || transaction.type === "withdrawal"
                    ? "-"
                    : ""}
                {transaction.amount.toFixed(4)} {transaction.currency}
              </p>
              <p className="text-xs text-sa-text-dim">${transaction.amount.toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
